import React from "react";
import { Link, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Check, Minus, Star } from "lucide-react";
import {
  Breadcrumbs, Container, Eyebrow, H1, H2, H3, Lede, Section, Spinner, EmptyState,
} from "../components/Primitives";
import { getService } from "../lib/api";
import { GuaranteeLine } from "../components/GuaranteeLine";
import { useCurrency } from "../lib/currency";
import { useSEO, breadcrumbSchema } from "../lib/seo";
import { Reveal, Stagger, StaggerItem } from "../lib/motion";

export default function ConsultService() {
  const { slug } = useParams();
  const { format } = useCurrency();
  const { data, isLoading, isError } = useQuery({
    queryKey: ["service", slug],
    queryFn: () => getService(slug),
  });

  const s = data?.service;

  useSEO({
    title: s ? `${s.branded_name} · ${s.seo_subtitle}` : "Consultation",
    description: s
      ? `${s.seo_subtitle}. ${s.duration_minutes >= 240 ? "Four 60-minute sessions a year" : `${s.duration_minutes} minutes`} with a named consultant for a flat ₹${s.price_inr.toLocaleString("en-IN")}, stack value ₹${s.stack_value_total.toLocaleString("en-IN")}. No per-minute billing, no wallet. Your consultant earns nothing from what you buy.`
      : undefined,
    jsonLd: s
      ? [
          breadcrumbSchema([
            { label: "Home", to: "/" },
            { label: "Consult", to: "/consult" },
            { label: s.branded_name },
          ]),
          {
            "@context": "https://schema.org",
            "@type": "Service",
            name: s.branded_name,
            alternateName: s.seo_subtitle,
            serviceType: s.seo_subtitle,
            provider: { "@type": "Organization", name: "Shreem Mantra Energy" },
            offers: {
              "@type": "Offer",
              price: s.price_inr,
              priceCurrency: "INR",
              url: `https://shreemmantraenergy.com/consult/${s.slug}`,
              itemOffered: s.stack_items.map((i) => ({
                "@type": "Service", name: i.label,
                offers: { "@type": "Offer", price: i.value_inr, priceCurrency: "INR" },
              })),
              additionalProperty: s.stack_items.map((i) => ({
                "@type": "PropertyValue", name: i.label, value: `₹${i.value_inr}`,
              })),
            },
          },
        ]
      : undefined,
  });

  if (isLoading) return <Container className="py-20"><Spinner /></Container>;
  if (isError || !s) {
    return (
      <Container className="py-20">
        <EmptyState title="No such consultation">
          <Link to="/consult" className="link-u">See all consultations →</Link>
        </EmptyState>
      </Container>
    );
  }

  return (
    <>
      <Container className="pt-6">
        <Breadcrumbs trail={[{ label: "Home", to: "/" }, { label: "Consult", to: "/consult" }, { label: s.branded_name }]} />
      </Container>

      <Section className="border-b border-rule pb-12">
        <Container className="grid items-start gap-12 lg:grid-cols-[1.1fr_0.9fr] lg:gap-16">
          <div>
            <Eyebrow>{s.seo_subtitle}</Eyebrow>
            <H1 className="mt-5">{s.branded_name}</H1>
            <p className="num mt-4 text-base text-ink">{s.headline}</p>
            <Lede className="mt-6">{s.lede}</Lede>

            <div className="mt-8 flex flex-wrap items-center gap-4">
              <Link to={`/consult/book/${s.slug}`} className="btn btn-primary" data-testid="book-cta">
                Book {s.branded_name} — {format(s.price_inr)}
              </Link>
              <Link to="/promises" className="link-u ui text-sm">The Guarantee Stack →</Link>
            </div>
            <GuaranteeLine testId="service-guarantee" className="mt-4">
              {s.slug === "suitability-session"
                ? "Credited back in full against any purchase over ₹5,000 within 30 days."
                : "One flat fee, no meter — and if a consultant tries to sell you a product on the call, the session is refunded in full."}
            </GuaranteeLine>

            {(s.body || []).map((b) => (
              <Reveal key={b.h} className="mt-12">
                <H2>{b.h}</H2>
                <p className="measure ui mt-4 text-sm leading-[1.7] text-dim">{b.p}</p>
              </Reveal>
            ))}

            <div className="mt-12 grid gap-8 sm:grid-cols-2">
              <div>
                <H3 className="mb-3">What's included</H3>
                <ul className="ui space-y-2 text-sm text-dim">
                  {(s.includes || []).map((x) => (
                    <li key={x} className="flex gap-2">
                      <Check className="mt-0.5 h-4 w-4 shrink-0 text-verify" aria-hidden="true" />{x}
                    </li>
                  ))}
                </ul>
              </div>
              <div>
                <H3 className="mb-3">What's not included</H3>
                <ul className="ui space-y-2 text-sm text-dim">
                  {(s.excludes || []).map((x) => (
                    <li key={x} className="flex gap-2">
                      <Minus className="mt-0.5 h-4 w-4 shrink-0 text-dim" aria-hidden="true" />{x}
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>

          <div className="lg:sticky lg:top-28 lg:self-start">
            <div className="card-s elev p-6" data-testid="value-stack">
              <p className="eyebrow mb-4">What's in the stack</p>
              <table className="w-full text-sm">
                <tbody>
                  {s.stack_items.map((i) => (
                    <tr key={i.label} className="border-b border-rule/70">
                      <td className="ui py-3 pr-4 text-dim">{i.label}</td>
                      <td className="num py-3 text-right text-ink">{format(i.value_inr)}</td>
                    </tr>
                  ))}
                  <tr className="border-b border-rule">
                    <td className="ui py-3 pr-4 font-semibold text-ink">Total stack value</td>
                    <td className="num py-3 text-right font-semibold text-ink">{format(s.stack_value_total)}</td>
                  </tr>
                  <tr>
                    <td className="ui py-3 pr-4 font-semibold text-gold">You pay</td>
                    <td className="num py-3 text-right text-lg font-semibold text-gold">{format(s.price_inr)}</td>
                  </tr>
                </tbody>
              </table>
              <Link to={`/consult/book/${s.slug}`} className="btn btn-primary mt-6 w-full" data-testid="book-cta-side">
                Book now
              </Link>
              <GuaranteeLine testId="service-guarantee-side" className="mt-4">
                {s.slug === "suitability-session"
                  ? "Credited back in full against any purchase over ₹5,000 within 30 days."
                  : "If a consultant sells you a product on the call, the session is refunded in full."}
              </GuaranteeLine>
              <p className="ui mt-4 text-xs text-dim">
                One flat price, tax inclusive, in your currency. No wallet, no stored balance, no
                per-minute billing, and no upsell at the payment step.
              </p>
            </div>

            <div className="card-s mt-6 p-6">
              <p className="eyebrow mb-4">Your consultant</p>
              <ul className="space-y-4">
                {(data.consultants || []).map((c) => (
                  <li key={c.slug} className="flex gap-4">
                    <img src={c.photo} alt={c.display_name} loading="lazy" className="h-16 w-16 shrink-0 object-cover" />
                    <div>
                      <p className="ui text-sm font-medium text-ink">{c.display_name}</p>
                      <p className="num text-xs text-dim">{c.years_practising} years · {c.languages.join(", ")}</p>
                      <p className="ui mt-1 text-xs text-dim">{c.specialisms.join(" · ")}</p>
                    </div>
                  </li>
                ))}
              </ul>
              <p className="ui mt-4 border-t border-rule pt-4 text-xs text-dim">
                No ratings, no badges, no "celebrity" labels. Commission rate: <span className="num">0</span>.
                It is a hardcoded constant in our systems, not a setting somebody could change.
              </p>
            </div>
          </div>
        </Container>
      </Section>

      {data.reviews?.length > 0 && (
        <Section>
          <Container>
            <H2>What verified buyers said</H2>
            <Stagger className="mt-8 grid gap-5 md:grid-cols-3" stagger={0.08}>
              {data.reviews.map((r) => (
                <StaggerItem key={`${r.author}-${r.date}`}>
                  <article className="card-s elev h-full p-7">
                    <div className="flex gap-1">
                      {[1, 2, 3, 4, 5].map((n) => (
                        <Star key={n} className={`h-3.5 w-3.5 ${n <= r.rating ? "fill-gold text-gold" : "text-rule"}`} aria-hidden="true" />
                      ))}
                    </div>
                    <H3 className="mt-4">{r.title}</H3>
                    <p className="ui mt-2.5 text-sm leading-[1.7] text-dim">{r.body}</p>
                    <p className="ui mt-5 border-t border-rule pt-4 text-xs text-dim">{r.author} · {r.location} · Verified purchase</p>
                  </article>
                </StaggerItem>
              ))}
            </Stagger>
          </Container>
        </Section>
      )}
    </>
  );
}
